/**
 * URL templating stage of the PostHog `before_send` hook.
 *
 * Rewrites every URL the SDK attaches to an event to the route template that
 * produced it (`/members/<id>` → `/members/:id`), with the same sanitiser and
 * the same fail-closed rule the Datadog providers use: a path that matches no
 * registered template is destroyed, never passed through.
 *
 * Runs last in the hook, after every stage that matches raw paths, so those
 * stages keep seeing the URL the browser was on.
 */

import { sanitizeUrl, type UrlTemplateSets } from '../redact';
import { compileRouteTemplates } from '../route-template-match';

/**
 * A URL-bearing property: posthog-js names them `$current_url`, `$pathname`,
 * `$referrer`, and derives `$initial_*`, `$session_entry_*` and
 * `$prev_pageview_*` forms from them, so the suffix is the stable part.
 */
const URL_KEY = /^\$.*(?:url|pathname|referrer)$/i;

/** posthog-js's marker for "no referrer"; not a URL. */
const DIRECT = '$direct';

/** A web-vitals metric object, which carries the URL it was measured on. */
const WEB_VITALS_METRIC = /^\$web_vitals_.+_event$/;

const HEATMAP_EVENT = '$$heatmap';

interface TemplatableEvent {
  event: string;
  properties: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function templateUrlKeys(record: unknown, sets: UrlTemplateSets): void {
  if (!isRecord(record)) return;
  for (const [key, value] of Object.entries(record)) {
    if (!URL_KEY.test(key) || typeof value !== 'string' || value === DIRECT) continue;
    record[key] = sanitizeUrl(value, 'page', sets);
  }
}

/**
 * Re-key heatmap points by templated URL. Entries for two pages of one route
 * collapse onto the same key, so their points are concatenated. Returns false
 * when the payload cannot be read.
 */
function templateHeatmap(properties: Record<string, unknown>, sets: UrlTemplateSets): boolean {
  const data = properties.$heatmap_data;
  if (data === undefined) return true;
  if (!isRecord(data)) return false;
  const out: Record<string, unknown> = {};
  for (const [url, points] of Object.entries(data)) {
    const key = sanitizeUrl(url, 'page', sets);
    const existing = out[key];
    if (existing === undefined) {
      out[key] = points;
      continue;
    }
    if (!isArray(existing) || !isArray(points)) return false;
    out[key] = [...existing, ...points];
  }
  properties.$heatmap_data = out;
  return true;
}

/**
 * Build the stage for a set of page route templates, compiled once. Undefined
 * when no templates are configured, so the hook skips the stage entirely.
 * The stage rewrites the event in place and returns false when the event must
 * be dropped; it never throws.
 */
export function createUrlTemplateStage(
  templates: readonly string[] | undefined,
): ((event: TemplatableEvent) => boolean) | undefined {
  if (templates === undefined) return undefined;
  const sets: UrlTemplateSets = { page: compileRouteTemplates(templates) };
  return (event) => {
    try {
      const { properties } = event;
      templateUrlKeys(properties, sets);
      // Person properties travel top-level on most events, but a `$set` event
      // (`setPersonProperties()`, and `identify()` for an already identified
      // user) carries them inside `properties`.
      for (const people of [event.$set, event.$set_once, properties.$set, properties.$set_once]) {
        templateUrlKeys(people, sets);
      }
      for (const [key, metric] of Object.entries(properties)) {
        if (WEB_VITALS_METRIC.test(key)) templateUrlKeys(metric, sets);
      }
      if (event.event === HEATMAP_EVENT) return templateHeatmap(properties, sets);
      return true;
    } catch {
      return false;
    }
  };
}
